// src/store/recordingsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized axios instance

// Fetch all recordings
export const fetchRecordings = createAsyncThunk("recordings/fetchAll", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get("/recordings/");
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// Fetch recordings by ICT ID (optional)
export const fetchRecordingsByICTId = createAsyncThunk("recordings/fetchByICTId", async (ictId, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(`/recordings/?ictId_id=${ictId}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

// Insert new recording
export const insertRecording = createAsyncThunk("recordings/insert", async (recording, { rejectWithValue }) => {
  try {
    const response = await apiClient.post("/recordings/", recording);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// Update existing recording
export const updateRecording = createAsyncThunk("recordings/update", async (recording, { rejectWithValue }) => {
  try {
    const response = await apiClient.put(`/recordings/${recording.id}/`, recording);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// Delete a recording
export const deleteRecording = createAsyncThunk("recordings/delete", async (id, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/recordings/${id}/`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

// Toggle recording status
export const toggleRecordingStatus = createAsyncThunk("recordings/toggleStatus", async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(`/recordings/${id}/status`, { recordingStatus: status });
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

const recordingsSlice = createSlice({
  name: "recordings",
  initialState: {
    recordings: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecordings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordings.fulfilled, (state, action) => {
        state.loading = false;
        state.recordings = action.payload;
      })
      .addCase(fetchRecordings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchRecordingsByICTId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordingsByICTId.fulfilled, (state, action) => {
        state.loading = false;
        state.recordings = action.payload;
      })
      .addCase(fetchRecordingsByICTId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(insertRecording.fulfilled, (state, action) => {
        state.recordings.push(action.payload);
      })

      .addCase(updateRecording.fulfilled, (state, action) => {
        const index = state.recordings.findIndex((rec) => rec.id === action.payload.id);
        if (index !== -1) state.recordings[index] = action.payload;
      })

      .addCase(deleteRecording.fulfilled, (state, action) => {
        state.recordings = state.recordings.filter((rec) => rec.id !== action.payload);
      })

      .addCase(toggleRecordingStatus.fulfilled, (state, action) => {
        const index = state.recordings.findIndex((rec) => rec.id === action.payload.id);
        if (index !== -1) state.recordings[index] = action.payload;
      });
  },
});

export default recordingsSlice.reducer;
